import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  useAllWorkersClockStatus,
  useTimeEntries,
  useClockAction,
  useDeleteTimeEntry,
  getEventTypeLabel,
  getEventTypeColor,
  getStatusLabel,
  getStatusColor,
} from '@/hooks/useTimeTracking';
import {
  formatMinutesToTime,
  getBreakReasonConfig,
  BREAK_REASONS,
  BreakReason,
} from '@/types/timeTracking';
import {
  Clock,
  Users,
  Search,
  UserCheck,
  UserX,
  Coffee,
  Timer,
  Calendar,
  Download,
  Filter,
  Trash2,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  History,
  Plus,
  Shield,
  PenLine,
} from 'lucide-react';
import { TimeTrackingExport } from './TimeTrackingExport';
import { TimeSignatureCard } from './TimeSignatureCard';
import { TimeTrackingAuditDashboard } from './TimeTrackingAuditDashboard';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface TimeTrackingPanelProps {
  embedded?: boolean;
}

export function TimeTrackingPanel({ embedded = false }: TimeTrackingPanelProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month'>('today');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);

  const { data: workersStatus, isLoading } = useAllWorkersClockStatus();
  const deleteEntryMutation = useDeleteTimeEntry();
  const clockAction = useClockAction();

  // Calculate date range
  const getDateRange = () => {
    const today = new Date();
    switch (dateRange) {
      case 'week':
        return {
          start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      case 'month':
        return {
          start: format(startOfMonth(today), 'yyyy-MM-dd'),
          end: format(endOfMonth(today), 'yyyy-MM-dd'),
        };
      default:
        return {
          start: format(today, 'yyyy-MM-dd'),
          end: format(today, 'yyyy-MM-dd'),
        };
    }
  };

  const { start, end } = getDateRange();
  const { data: timeEntries } = useTimeEntries(selectedUserId || undefined, start, end);

  // Filter workers
  const filteredWorkers = workersStatus?.filter((worker) => {
    const matchesSearch =
      worker.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.area?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      worker.current_status === statusFilter;

    return matchesSearch && matchesStatus;
  }) || [];

  // Stats
  const stats = {
    total: workersStatus?.length || 0,
    working: workersStatus?.filter((w) => w.current_status === 'working').length || 0,
    onBreak: workersStatus?.filter((w) => w.current_status === 'on_break').length || 0,
    clockedOut: workersStatus?.filter((w) => w.current_status === 'clocked_out').length || 0,
  };

  const handleViewHistory = (userId: string) => {
    setSelectedUserId(userId);
    setHistoryDialogOpen(true);
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!confirm('¿Estás seguro de eliminar este registro?')) return;

    try {
      await deleteEntryMutation.mutateAsync(entryId);
      toast({
        title: 'Registro eliminado',
        description: 'El registro de fichaje ha sido eliminado',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const [activeTab, setActiveTab] = useState('dashboard');

  const content = (
    <div className={cn("space-y-6", !embedded && "p-6")}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-magma bg-clip-text text-transparent flex items-center gap-2">
            <Clock className="w-6 h-6 text-primary" />
            Control de Fichaje
          </h2>
          <p className="text-muted-foreground mt-1">
            Gestión de fichajes conforme a RDL 8/2019 (Ley española de control horario)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={(v: any) => setDateRange(v)}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="week">Semana</SelectItem>
              <SelectItem value="month">Mes</SelectItem>
            </SelectContent>
          </Select>
          <TimeTrackingExport />
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="dashboard" className="gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="signature" className="gap-2">
            <PenLine className="w-4 h-4" />
            <span className="hidden sm:inline">Mi Firma</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Auditoría</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="signature" className="mt-6">
          <TimeSignatureCard />
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <TimeTrackingAuditDashboard />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-6 space-y-6">

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Personal</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-green-200 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Trabajando</p>
                <p className="text-2xl font-bold text-green-600">{stats.working}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-yellow-200 dark:border-yellow-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Pausa</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.onBreak}</p>
              </div>
              <Coffee className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-gray-200 dark:border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fuera</p>
                <p className="text-2xl font-bold text-gray-500">{stats.clockedOut}</p>
              </div>
              <UserX className="w-8 h-8 text-gray-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, ID o área..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
          >
            Todos
          </Button>
          <Button
            variant={statusFilter === 'working' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('working')}
            className={statusFilter === 'working' ? 'bg-green-600' : ''}
          >
            <UserCheck className="w-4 h-4 mr-1" />
            Trabajando
          </Button>
          <Button
            variant={statusFilter === 'on_break' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('on_break')}
            className={statusFilter === 'on_break' ? 'bg-yellow-600' : ''}
          >
            <Coffee className="w-4 h-4 mr-1" />
            En Pausa
          </Button>
          <Button
            variant={statusFilter === 'clocked_out' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('clocked_out')}
          >
            <UserX className="w-4 h-4 mr-1" />
            Fuera
          </Button>
        </div>
      </div>

      {/* Workers Table */}
      <Card className="glass-card">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No se encontraron trabajadores</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trabajador</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último Evento</TableHead>
                  <TableHead>Tiempo Trabajado</TableHead>
                  <TableHead>Pausas</TableHead>
                  <TableHead>Turno</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.map((worker) => {
                  const breakConfig = worker.current_break_reason
                    ? getBreakReasonConfig(worker.current_break_reason)
                    : null;

                  return (
                    <TableRow key={worker.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-magma flex items-center justify-center text-white font-bold">
                            {worker.full_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{worker.full_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {worker.employee_id || worker.area || '-'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={cn(getStatusColor(worker.current_status))}>
                            {getStatusLabel(worker.current_status)}
                          </Badge>
                          {breakConfig && (
                            <span className="text-xs text-muted-foreground">
                              {breakConfig.label}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {worker.last_event_time ? (
                          <span className="text-sm">
                            {format(new Date(worker.last_event_time), 'HH:mm')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Timer className="w-4 h-4 text-green-500" />
                          <span className="font-mono">
                            {formatMinutesToTime(worker.today_worked_minutes)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Coffee className="w-4 h-4 text-yellow-500" />
                          <span className="font-mono">
                            {formatMinutesToTime(worker.today_break_minutes)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {worker.shift_name ? (
                          <Badge variant="outline">{worker.shift_name}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewHistory(worker.user_id)}
                        >
                          <History className="w-4 h-4 mr-1" />
                          Historial
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Break Reasons Legend */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Tipos de Pausa - Normativa UE 2019/1152
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {BREAK_REASONS.map((reason) => (
              <div
                key={reason.code}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: reason.color }}
                />
                <span className="text-sm">{reason.label}</span>
                <Badge
                  variant={reason.is_paid ? 'default' : 'secondary'}
                  className="text-xs ml-auto"
                >
                  {reason.is_paid ? 'Rem.' : 'No rem.'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Fichajes
            </DialogTitle>
            <DialogDescription>
              Registros del período seleccionado
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {timeEntries && timeEntries.length > 0 ? (
              timeEntries.map((entry) => {
                const breakConfig = entry.break_reason
                  ? getBreakReasonConfig(entry.break_reason)
                  : null;

                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <Badge className={cn(getEventTypeColor(entry.event_type))}>
                        {getEventTypeLabel(entry.event_type)}
                      </Badge>
                      <div>
                        <p className="font-medium">
                          {format(new Date(entry.event_time), "dd/MM/yyyy 'a las' HH:mm")}
                        </p>
                        {breakConfig && (
                          <p className="text-sm text-muted-foreground">
                            Motivo: {breakConfig.label}
                          </p>
                        )}
                        {entry.notes && (
                          <p className="text-sm text-muted-foreground">
                            Notas: {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.is_manual && (
                        <Badge variant="outline" className="text-xs">
                          Manual
                        </Badge>
                      )}
                      {entry.approved_by && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay registros en este período
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return content;
}

export default TimeTrackingPanel;
